import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Gift,
  Activity,
  PoundSterling,
  ClipboardList,
  HeartPulse,
  LogOut,
  Search,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/comps", label: "Comps", icon: Gift, testid: "admin-nav-comps" },
  { to: "/admin/activity", label: "Activity", icon: Activity, testid: "admin-nav-activity" },
  { to: "/admin/costs", label: "Costs", icon: PoundSterling, testid: "admin-nav-costs" },
  { to: "/admin/audit", label: "Audit log", icon: ClipboardList, testid: "admin-nav-audit" },
  { to: "/admin/system", label: "System", icon: HeartPulse, testid: "admin-nav-system" },
];

const ENV_BADGE = (process.env.REACT_APP_ENV || "PROD").toUpperCase();

function AdminSidebarBody({ user, logout, onItemClick }) {
  return (
    <>
      <Link
        to="/admin"
        onClick={onItemClick}
        className="flex items-center gap-2.5 px-2 py-2"
      >
        <span
          className="brand-mark inverted"
          style={{ background: "white", color: "black", width: 28, height: 28, fontSize: 18 }}
        >
          A
        </span>
        <span
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: "-0.01em",
          }}
        >
          Admin
        </span>
      </Link>
      <div className="mt-1 px-2">
        <span
          className="mono"
          style={{
            fontSize: 9,
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            color: "#f59e0b",
            border: "1px solid rgba(245, 158, 11, 0.4)",
            padding: "1px 6px",
            borderRadius: 4,
          }}
        >
          {ENV_BADGE}
        </span>
      </div>

      <nav className="mt-5 space-y-0.5">
        {NAV.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/admin"}
            data-testid={testid}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] transition-colors ${
                isActive
                  ? "bg-white text-black"
                  : "text-zinc-300 hover:bg-zinc-900 hover:text-white"
              }`
            }
          >
            <Icon size={14} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto border-t border-zinc-800 pt-3 px-2">
        <div className="flex items-center gap-2 py-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-7 h-7 rounded-full"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-zinc-700" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-medium truncate">
              {user?.name || user?.email}
            </div>
            <div className="text-[10px] text-zinc-500 truncate">{user?.email}</div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Link
            to="/dashboard"
            onClick={onItemClick}
            className="text-[11px] text-zinc-400 hover:text-white"
            data-testid="admin-exit"
          >
            Exit to app
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white"
          >
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Dark, dense admin shell. A power tool, not a marketing surface.
 */
export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [focused, setFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/users?q=${encodeURIComponent(q)}&limit=8`);
        setResults(r.data.users || []);
      } catch {
        setResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="admin-shell">
      {/* Desktop sidebar */}
      <aside
        className="fixed inset-y-0 left-0 w-[220px] bg-black border-r border-zinc-800 px-3 py-4 hidden lg:flex flex-col"
        data-testid="admin-sidebar"
      >
        <AdminSidebarBody user={user} logout={logout} />
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/60 lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="admin-mobile-backdrop"
          />
          <aside
            className="fixed inset-y-0 left-0 z-50 w-[240px] bg-black border-r border-zinc-800 px-3 py-4 flex flex-col lg:hidden"
            data-testid="admin-mobile-drawer"
          >
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="admin-mobile-close"
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            <AdminSidebarBody
              user={user}
              logout={logout}
              onItemClick={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      <header className="lg:pl-[220px] sticky top-0 z-30 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="h-14 px-5 sm:px-6 flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 -ml-2 text-zinc-300"
            aria-label="Open menu"
            data-testid="admin-mobile-toggle"
          >
            <Menu size={20} />
          </button>

          <div className="relative w-full max-w-md">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 200)}
              placeholder="Search users"
              data-testid="admin-search"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-8 pr-3 py-2 text-xs focus:outline-none focus:border-zinc-600"
            />
            {focused && results.length > 0 && (
              <div
                className="absolute left-0 right-0 mt-1.5 bg-zinc-950 border border-zinc-800 rounded-lg shadow-lg z-40 overflow-hidden"
                data-testid="admin-search-results"
              >
                {results.map((u) => (
                  <button
                    key={u.user_id}
                    onMouseDown={() => navigate(`/admin/users/${u.user_id}`)}
                    data-testid={`admin-search-result-${u.user_id}`}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-900 border-b border-zinc-900 last:border-0"
                  >
                    <div className="font-medium">{u.email}</div>
                    <div className="text-zinc-500 mono text-[10px] uppercase tracking-[0.18em]">
                      {u.plan} · {u.project_count} proj · {u.last_login_bucket?.replace("_", " ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ml-auto hidden sm:flex items-center gap-2 text-[10px] text-zinc-500 mono uppercase tracking-[0.22em]">
            <span>Admin console</span>
            <span>·</span>
            <span>v0.1</span>
          </div>
        </div>
      </header>

      <main className="lg:pl-[220px]">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-6 py-5 sm:py-6">{children}</div>
      </main>
    </div>
  );
}
